import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import { defineStore } from 'pinia'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Popover as VanPopover,
  Popup as VanPopup,
  Picker as VanPicker,
  Row as VanRow,
  Col as VanCol,
} from 'vant'
import {
  BALL_AMAGE_MAP,
  PlusSvg,
  compareBallNumbers,
  padBallNumber,
  toDrawBalls,
} from '@/components/lottery-ball-row'
import type { LotteryBall } from '@/components/lottery-ball-row'
import { useRegionStore } from '@/stores/region-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { lotteryName } from '@/utils/seo'
import { getApiV1LotteryYears, getApiV1LotterySearch49208 } from '@/api/sdk.gen'
import type { LotteryRecord } from '@/api/types.gen'
import LotterySwitcher from '@/components/lottery-switcher'

// ==================== 类型定义 ====================

type SortOrder = 'desc' | 'asc'
type BallsOrder = 'original' | 'asc' | 'desc'

interface HistoryItem {
  issueNo: string
  periodStr: string
  date: string
  balls: LotteryBall[]
  specialBall: LotteryBall
}

// ==================== 常量 ====================

const CURRENT_YEAR = new Date().getFullYear()
const PAGE_SIZE = 30

// ==================== 辅助函数 ====================


function sortBalls(balls: LotteryBall[], order: BallsOrder): LotteryBall[] {
  if (order === 'original') return balls
  return [...balls].sort((a, b) => compareBallNumbers(a, b, order))
}

// ==================== Pinia Store ====================

const useHistoryStore = defineStore('history-store', () => {
  const regionStore = useRegionStore()

  const yearOptions = ref<{ label: string; value: number }[]>([])
  const selectedYear = ref(CURRENT_YEAR)
  const sortOrder = ref<SortOrder>('desc')
  const showWuxing = ref(true)
  const ballsOrder = ref<BallsOrder>('original')
  const rawList = ref<HistoryItem[]>([])
  const loading = ref(false)
  const currentPage = ref(1)

  const list = computed(() => rawList.value)

  const hasMore = ref(false)

  async function fetchYears() {
    const res = await getApiV1LotteryYears({
      query: { region: regionStore.region },
    })
    const years = res.data?.data ?? []
    yearOptions.value = years.map((y) => ({ label: `${y}年`, value: y }))
    // 若当前选中年份不在列表中，重置为第一个
    if (years.length > 0 && !years.includes(selectedYear.value)) {
      selectedYear.value = years[0]!
    }
  }

  function mapRecord(r: LotteryRecord): HistoryItem {
    return {
      issueNo: `${r.year}${r.periodStr}`,
      periodStr: r.periodStr ?? '',
      date: r.lotteryTime ?? '',
      ...toDrawBalls(r.numberList ?? []),
    }
  }

  async function fetchPage(page: number) {
    const res = await getApiV1LotterySearch49208({
      query: {
        region: regionStore.region,
        year: selectedYear.value,
        pageNum: page,
        pageSize: PAGE_SIZE,
        sort: sortOrder.value === 'asc' ? 1 : 2,
      },
    })
    return res.data?.data?.recordList ?? []
  }

  async function loadAll() {
    if (loading.value) return
    loading.value = true
    currentPage.value = 1
    const records = await fetchPage(1)
    rawList.value = records.map(mapRecord)
    hasMore.value = records.length >= PAGE_SIZE
    loading.value = false
  }

  async function loadMore() {
    if (loading.value || !hasMore.value) return
    loading.value = true
    currentPage.value += 1
    const records = await fetchPage(currentPage.value)
    rawList.value = [...rawList.value, ...records.map(mapRecord)]
    hasMore.value = records.length >= PAGE_SIZE
    loading.value = false
  }

  function setYear(year: number) {
    selectedYear.value = year
    loadAll()
  }

  function setSortOrder(order: SortOrder) {
    sortOrder.value = order
    loadAll()
  }

  // region 变化时重新拉取年份和数据
  watch(
    () => regionStore.region,
    async () => {
      await fetchYears()
      await loadAll()
    },
  )

  return {
    yearOptions,
    selectedYear,
    sortOrder,
    showWuxing,
    ballsOrder,
    list,
    hasMore,
    loading,
    fetchYears,
    loadAll,
    loadMore,
    setYear,
    setSortOrder,
  }
})

// ==================== 子组件 ====================

const HistoryHeader = defineComponent({
  props: {
    showRightIcon: { type: Boolean, default: true },
  },
  emits: ['toggle-year-picker'],
  setup(props, { emit }) {
  const goBack = useSmartBack()
    return () => (
      <VanNavBar fixed title="历史开奖">
        {{
          left: () => (
            <VanIcon name="arrow-left" color="#333" size="16" onClick={() => goBack()} />
          ),
          right: () => (
            <div class="flex items-center gap-1.5">
              {props.showRightIcon && (
                <VanIcon
                  name="notes-o"
                  color="#07c160"
                  size="16"
                  onClick={() => emit('toggle-year-picker')}
                />
              )}
              <LotterySwitcher />
            </div>
          ),
        }}
      </VanNavBar>
    )
  },
})

const HistoryToolbar = defineComponent({
  setup() {
    const store = useHistoryStore()
    const showSortPopover = ref(false)

    const sortActions = [
      { text: '默认', sort: 'original' as BallsOrder },
      { text: '平码升序', sort: 'asc' as BallsOrder },
      { text: '平码降序', sort: 'desc' as BallsOrder },
    ]

    const currentSortLabel = computed(() => {
      return sortActions.find((a) => a.sort === store.ballsOrder)?.text ?? '默认'
    })

    return () => (
      <div class="head-find flex items-center justify-between gap-2">
        <div class="flex-1 min-w-0 truncate">{store.selectedYear}年历史开奖记录</div>
        <div class="right-find shrink-0">
          {/* 样式挂在 `.right-find > span` 上：span 保留，按钮放里面 */}
          <span class="border">
            <button
              type="button"
              class="btn-reset"
              onClick={() => store.setSortOrder(store.sortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {store.sortOrder === 'desc' ? '降序' : '升序'}
            </button>
          </span>
          <span class={store.showWuxing ? 'act' : ''}>
            <button type="button" class="btn-reset" onClick={() => (store.showWuxing = !store.showWuxing)}>
              五行
            </button>
          </span>
          <VanPopover
            show={showSortPopover.value}
            onUpdate:show={(v: boolean) => (showSortPopover.value = v)}
            actions={sortActions}
            placement="bottom-end"
            onSelect={(action: { text: string; sort: BallsOrder }) => {
              store.ballsOrder = action.sort
              showSortPopover.value = false
            }}
          >
            {{
              reference: () => <span>{currentSortLabel.value}</span>,
            }}
          </VanPopover>
        </div>
      </div>
    )
  },
})

const HistoryList = defineComponent({
  setup() {
    const store = useHistoryStore()
    const sentinelRef = ref<HTMLDivElement>()
    let observer: IntersectionObserver | null = null

    onMounted(() => {
      if (!sentinelRef.value) return
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && store.hasMore) store.loadMore()
        },
        { rootMargin: '200px' },
      )
      observer.observe(sentinelRef.value)
    })

    onUnmounted(() => observer?.disconnect())

    return () => (
      <div class="body-list">
        {store.list.length === 0 && !store.loading && (
          <div class="py-[.6rem] text-center text-[.28rem] text-[#999]">暂无数据</div>
        )}

        {store.list.map((item) => (
          <div key={item.issueNo} class="body-list-item">
            <div class="hd-text">
              <VanRow>
                <VanCol span={16}>第{item.periodStr}期最新开奖结果</VanCol>
                <VanCol span={8}>
                  <span>{item.date}</span>
                </VanCol>
              </VanRow>
            </div>
            <RouterLink class="border-box cursor-pointer btn-reset block" to={`/lottery/${item.issueNo}`}>
              <div class="border-box-body">
                <div class="border-box-body-bd">
                  <VanRow>
                    {sortBalls(item.balls, store.ballsOrder).map((ball) => (
                      <VanCol key={ball.number} class="tp left">
                        <div class={`icon-text bg-amage-${BALL_AMAGE_MAP[ball.color]}`}>
                          <span class="slot-icon">
                            <span>{padBallNumber(ball.number)}</span>
                          </span>
                          <div class="slot-text">
                            {ball.zodiac}
                            {store.showWuxing ? `/${ball.wuxing}` : ''}
                          </div>
                        </div>
                      </VanCol>
                    ))}
                    <VanCol class="tc">
                      <div class="connect">
                        <PlusSvg />
                      </div>
                    </VanCol>
                    <VanCol class="tp right">
                      <div class={`icon-text bg-amage-${BALL_AMAGE_MAP[item.specialBall.color]}`}>
                        <span class="slot-icon">
                          <span>{padBallNumber(item.specialBall.number)}</span>
                        </span>
                        <div class="slot-text">
                          {item.specialBall.zodiac}
                          {store.showWuxing ? `/${item.specialBall.wuxing}` : ''}
                        </div>
                      </div>
                    </VanCol>
                  </VanRow>
                </div>
              </div>
            </RouterLink>
          </div>
        ))}

        <div ref={sentinelRef} class="py-[.3rem] flex justify-center">
          {store.loading && (
            <div class="w-[.4rem] h-[.4rem] rounded-full border-2 border-[#ddd] border-t-[#07c160] animate-spin" />
          )}
          {!store.hasMore && store.list.length > 0 && (
            <span class="text-[.24rem] text-[#999]">没有更多了</span>
          )}
        </div>
      </div>
    )
  },
})

// ==================== 历史开奖页（默认导出） ====================

export default defineComponent({
  name: 'HistoryPage',
  setup() {
    const route = useRoute()
    const store = useHistoryStore()
    const regionStore = useRegionStore()
    const showYearPicker = ref(false)

    // SEO：开奖历史页，随彩种动态（站点名取后台配置）
    const brand = useSiteBrand()
    const lotName = computed(() => lotteryName(regionStore.lotteryType))
    useSeo({
      title: () => `${lotName.value}开奖历史记录_历年开奖号码查询 - ${brand.value}`,
      description: () =>
        `${lotName.value}历史开奖记录查询，提供历年各期开奖号码、特码与开奖时间，支持按年份与排序查看。`,
      keywords: () => `${lotName.value},开奖历史,历史开奖记录,开奖号码查询,${brand.value}`,
    })

    const pickerColumns = computed(() =>
      store.yearOptions.map((y) => ({ text: y.label, value: y.value })),
    )

    onMounted(async () => {
      const type = route.query.type
      if (type === 'hk' || type === 'macau') {
        regionStore.setLotteryType(type)
      }
      await store.fetchYears()
      await store.loadAll()
    })

    return () => (
      <div class="lottery-index">
        <HistoryHeader onToggle-year-picker={() => (showYearPicker.value = true)} />

        <div class="historical-list">
          <HistoryToolbar />
          <HistoryList />
        </div>

        <VanPopup
          show={showYearPicker.value}
          onUpdate:show={(v: boolean) => (showYearPicker.value = v)}
          position="bottom"
        >
          <VanPicker
            columns={pickerColumns.value}
            title="选择年份"
            onConfirm={({ selectedValues }: { selectedValues: number[] }) => {
              const year = selectedValues[0]
              if (year != null) store.setYear(year)
              showYearPicker.value = false
            }}
            onCancel={() => (showYearPicker.value = false)}
          />
        </VanPopup>
      </div>
    )
  },
})
